import axios from 'axios';
import { useToast } from '@chakra-ui/react';
import {
  QueryStatus, useMutation, useQuery, useQueryClient,
} from 'react-query';
import { NIL } from 'uuid';
import { Project } from '../models/project';
import { Proposal } from '../models/proposal';
import { PurchaseOrder } from '../models/purchaseOrder';
import { SqlDateTimeToDateField } from '../utils/SqlTools';
import {Invoice} from "../models/invoice";
import {Bill} from "../models/bill";

const getProjectById = async (id: string): Promise<Project> => {
  const apiUrl = process.env.REACT_APP_BASIX_API || '';
  const { data } = await axios.get(`${apiUrl}project/${id}`);

  data.proposals = data.proposals.map((p: Proposal) => {
    const proposal: Proposal = { ...p };
    // change date format to yyyy-MM-dd
    proposal.date = SqlDateTimeToDateField(proposal.date);
    return proposal;
  });
  data.purchaseOrders = data.purchaseOrders.map((p: PurchaseOrder) => {
    const purchaseOrder: PurchaseOrder = { ...p };

    purchaseOrder.date = SqlDateTimeToDateField(purchaseOrder.date);
    return purchaseOrder;
  });
  data.invoices = data.invoices.map((i: Invoice) => {
    const invoice: Invoice = { ...i };

    invoice.date = SqlDateTimeToDateField(invoice.date);
    return invoice;
  });
  data.bills = data.bills.map((b: Bill) => {
    const bills: Bill = { ...b };

    bills.date = SqlDateTimeToDateField(bills.date);
    bills.dueDate = SqlDateTimeToDateField(bills.dueDate);

    return bills;
  });
  return data;
};

// returns the project id — on CREATE the API hands back the new project's Guid
// (ProjectController.CreateProject → Guid), which the inline project picker uses to
// select the just-created project (see ProjectSelectField).
const postProject = async (project: Project): Promise<string | undefined> => {
  const apiUrl = process.env.REACT_APP_BASIX_API || '';
  if (project.id !== '' && project.id !== undefined) {
    await axios.post(`${apiUrl}project/${project.id}`, project);
    return project.id;
  }
  const { data } = await axios.post(`${apiUrl}project`, project);
  const newId = data as string;
  // item 1276 — CREATE-DROP IMMUNITY (same as useDocument): re-post the full values as an
  // update onto the new id, so any field the create ctor ignores persists via the update
  // path. Best-effort — the created project stands either way.
  if (newId) {
    try { await axios.post(`${apiUrl}project/${newId}`, { ...project, id: newId }); } catch { /* create stands */ }
  }
  return newId;
};

const closeProject = async (id: string) => {
  const apiUrl = process.env.REACT_APP_BASIX_API || '';
  await axios.post(`${apiUrl}project/closeProject/${id}`, id);
};

const reopenProject = async (id: string) => {
  const apiUrl = process.env.REACT_APP_BASIX_API || '';
  await axios.post(`${apiUrl}project/Reopen/${id}`, id);
};

// item 1166/1332 — "Hide from project sidebar" (Project.IsAdministrative). Admin-only endpoint.
const setAdministrative = async (id: string, value: boolean) => {
  const apiUrl = process.env.REACT_APP_BASIX_API || '';
  await axios.post(`${apiUrl}project/Administrative/${id}?value=${value}`);
};

const toggleFavorite = async (id: string) => {
  const apiUrl = process.env.REACT_APP_BASIX_API || '';
  await axios.post(`${apiUrl}project/favorite/${id}`, id);
};

interface ProjectProps {
  id: string;
  onSuccess?: any;
}
const useProject = (props: ProjectProps): {
  data: Project | undefined,
  status: QueryStatus,
  refetch: () => void,
  setProject: (data: Project) => Promise<unknown>,
  setProjectClosed: (id: string) => void,
  setProjectReopened: (id: string) => Promise<unknown>,
  setProjectFavorite: (id: string) => void,
  setProjectAdministrative: (id: string, value: boolean) => void, // item 1332
} => {
  const queryClient = useQueryClient();
  const toast = useToast(); // item 742 — surface blocked-close reasons
  const { status, data, refetch } = useQuery(['project', props.id], () => getProjectById(props.id), {
    onSuccess: (result) => {
      if (props.id === '') return;
      if (props.onSuccess === undefined) return;
      props.onSuccess(result);
    },
    refetchOnWindowFocus: false,
    // the project detail EMBEDS its proposals; with the global 30s staleTime, navigating to a
    // project served stale data (a re-parented proposal lingered on its old project). Always
    // refetch on mount so opening any project shows its CURRENT proposals. (dart 2026-07-14)
    refetchOnMount: 'always',
    staleTime: 0,
    enabled: props.id !== NIL,
  });

  const tryUpdate = useMutation((newData: Project) => postProject(newData), {
    onSuccess: async (result, variables) => {
      await queryClient.invalidateQueries('projects');
      await queryClient.invalidateQueries(['project', variables.id]);
    },
  });
  const setProject = (project: Project) => tryUpdate.mutateAsync(project);

  const tryClose = useMutation((id: string) => closeProject(id), {
    onSuccess: async (result, id) => {
      await queryClient.invalidateQueries('projects');
      await queryClient.invalidateQueries(['project', id]);
    },
    // item 742 (Chad) — a blocked close (e.g. the project has a live subscription) must SAY WHY,
    // not silently do nothing (the mutation previously swallowed errors).
    onError: (e: any) => {
      toast({
        status: 'error',
        title: 'Project not closed',
        description: String(e?.response?.data?.errors?.[0] ?? e?.response?.data ?? 'Try again.'),
        duration: 9000,
        isClosable: true,
      });
    },
  });
  const setProjectClosed = (id:string) => {
    tryClose.mutate(id);
  };

  const tryReopen = useMutation((id: string) => reopenProject(id), {
    onSuccess: async (result, id) => {
      await queryClient.invalidateQueries('projects');
      await queryClient.invalidateQueries(['project', id]);
      await queryClient.invalidateQueries('project');
    },
  });
  const setProjectReopened = (id:string) => tryReopen.mutateAsync(id);

  const tryToggleFavorite = useMutation((id: string) => toggleFavorite(id), {
    onSuccess: async (result, id) => {
      await queryClient.invalidateQueries('projects');
      await queryClient.invalidateQueries(['project', id]);
    },
  });
  const setProjectFavorite = (id:string) => {
    tryToggleFavorite.mutate(id);
  };

  // item 1332 (Chad) — hide/show a project in the sidebar tree (+ KPIs). Says why on failure.
  const tryAdministrative = useMutation(
    ({ id, value }: { id: string; value: boolean }) => setAdministrative(id, value),
    {
      onSuccess: async (result, { id }) => {
        await queryClient.invalidateQueries('projects');
        await queryClient.invalidateQueries(['project', id]);
      },
      onError: (e: any) => {
        toast({
          status: 'error',
          title: 'Project not updated',
          description: String(e?.response?.data ?? 'Admin only.'),
          duration: 6000,
          isClosable: true,
        });
      },
    },
  );
  const setProjectAdministrative = (id: string, value: boolean) => {
    tryAdministrative.mutate({ id, value });
  };

  return {
    data,
    status,
    refetch,
    setProject,
    setProjectClosed,
    setProjectReopened,
    setProjectFavorite,
    setProjectAdministrative,
  };
};

export default useProject;
